import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { TIP_DEFINITIONS, getTipDefinition, TipDefinition } from '../config/tipDefinitions';

// Re-export for convenience (type-only export avoids Babel scope issues)
export { TIP_DEFINITIONS, getTipDefinition };
export type { TipDefinition };

// =================== TYPES ===================

export interface TipsContextValue {
  /** Trigger a tip to display (ignored if already shown or tutorial is active) */
  showTip: (tipId: string) => void;
  /** Dismiss the currently active tip */
  dismissTip: () => void;
  /** Check if a tip has already been shown/dismissed */
  isTipShown: (tipId: string) => boolean;
  /** Currently displayed tip ID (null if none) */
  activeTip: string | null;
  /** Current tip's i18n message key */
  activeTipMessageKey: string;
  /** Current tip definition */
  activeTipDefinition: TipDefinition | null;
  /** Whether tips are suppressed (e.g. during tutorial) */
  isSuppressed: boolean;
  /** Suppress tips (used by tutorial system) */
  setSuppressed: (suppressed: boolean) => void;
}

// =================== CONSTANTS ===================

const STORAGE_KEY = 'tips_shown_v1';

// =================== CONTEXT ===================

const TipsContext = createContext<TipsContextValue | undefined>(undefined);

// =================== PROVIDER ===================

interface TipsProviderProps {
  children: React.ReactNode;
}

export function TipsProvider({ children }: TipsProviderProps) {
  const [shownTips, setShownTips] = useState<Record<string, boolean>>({});
  const [activeTip, setActiveTip] = useState<string | null>(null);
  const [isSuppressed, setIsSuppressed] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const autoDismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tipQueueRef = useRef<string[]>([]);

  // Load shown tips from AsyncStorage on mount
  useEffect(() => {
    const loadShownTips = async () => {
      try {
        const value = await AsyncStorage.getItem(STORAGE_KEY);
        if (value) {
          setShownTips(JSON.parse(value));
        }
      } catch (error) {
        console.warn('Failed to load tips state:', error);
      } finally {
        setIsLoaded(true);
      }
    };
    loadShownTips();
  }, []);

  // Save shown tips to AsyncStorage
  const saveShownTips = useCallback(async (tips: Record<string, boolean>) => {
    try {
      await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(tips));
    } catch (error) {
      console.warn('Failed to save tips state:', error);
    }
  }, []);

  // Clear auto-dismiss timer
  const clearAutoDismiss = useCallback(() => {
    if (autoDismissTimerRef.current) {
      clearTimeout(autoDismissTimerRef.current);
      autoDismissTimerRef.current = null;
    }
  }, []);

  // Process next tip in queue
  const processQueue = useCallback(() => {
    if (tipQueueRef.current.length > 0 && !activeTip) {
      const nextTip = tipQueueRef.current.shift();
      if (nextTip) {
        setActiveTip(nextTip);
      }
    }
  }, [activeTip]);

  // Show a tip
  const showTip = useCallback((tipId: string) => {
    if (!isLoaded) return;

    const definition = getTipDefinition(tipId);
    if (!definition) return;

    // Don't show if already shown and showOnce is true
    if (definition.showOnce && shownTips[tipId]) return;

    // Don't show if suppressed (tutorial active)
    if (isSuppressed) return;

    // If a tip is already showing, queue this one
    if (activeTip) {
      if (!tipQueueRef.current.includes(tipId)) {
        tipQueueRef.current.push(tipId);
      }
      return;
    }

    setActiveTip(tipId);
  }, [isLoaded, shownTips, isSuppressed, activeTip]);

  // Dismiss current tip
  const dismissTip = useCallback(() => {
    clearAutoDismiss();

    if (activeTip) {
      // Mark as shown
      const updatedTips = { ...shownTips, [activeTip]: true };
      setShownTips(updatedTips);
      saveShownTips(updatedTips);
      setActiveTip(null);

      // Process queue after a brief delay
      setTimeout(() => processQueue(), 300);
    }
  }, [activeTip, shownTips, saveShownTips, clearAutoDismiss, processQueue]);

  // Auto-dismiss timer
  useEffect(() => {
    if (!activeTip) return;

    const definition = getTipDefinition(activeTip);
    if (!definition) return;

    clearAutoDismiss();

    autoDismissTimerRef.current = setTimeout(() => {
      dismissTip();
    }, definition.autoDismissMs);

    return () => {
      clearAutoDismiss();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTip]);

  // Check if a tip has been shown
  const isTipShown = useCallback((tipId: string): boolean => {
    return !!shownTips[tipId];
  }, [shownTips]);

  // Suppress handler
  const setSuppressed = useCallback((suppressed: boolean) => {
    setIsSuppressed(suppressed);
    if (suppressed && activeTip) {
      // Dismiss active tip when suppressed
      clearAutoDismiss();
      setActiveTip(null);
    }
  }, [activeTip, clearAutoDismiss]);

  // Active tip definition
  const activeTipDefinition = useMemo(() => {
    if (!activeTip) return null;
    return getTipDefinition(activeTip) || null;
  }, [activeTip]);

  const activeTipMessageKey = useMemo(() => {
    return activeTipDefinition?.messageKey || '';
  }, [activeTipDefinition]);

  const value = useMemo<TipsContextValue>(() => ({
    showTip,
    dismissTip,
    isTipShown,
    activeTip,
    activeTipMessageKey,
    activeTipDefinition,
    isSuppressed,
    setSuppressed,
  }), [
    showTip,
    dismissTip,
    isTipShown,
    activeTip,
    activeTipMessageKey,
    activeTipDefinition,
    isSuppressed,
    setSuppressed,
  ]);

  return (
    <TipsContext.Provider value={value}>
      {children}
    </TipsContext.Provider>
  );
}

// =================== HOOKS ===================

const defaultContextValue: TipsContextValue = {
  showTip: () => {},
  dismissTip: () => {},
  isTipShown: () => false,
  activeTip: null,
  activeTipMessageKey: '',
  activeTipDefinition: null,
  isSuppressed: false,
  setSuppressed: () => {},
};

export function useTips() {
  const context = useContext(TipsContext);
  if (!context) {
    return defaultContextValue;
  }
  return context;
}
